import React, { useCallback, useMemo } from 'react';
import { Dayjs } from 'dayjs';
import { Form, DatePicker, FormItemProps, Grid } from 'antd';
import { DatePickerProps, RangePickerProps } from 'antd/lib/date-picker';
import { transformRules, Rule } from '../utils/rules';
import InfoTooltip from './InfoTooltip';

const { RangePicker } = DatePicker;

interface CommonDateProps {
  name: FormItemProps['name'];
  label: string;
  tooltip?: PropPresets.Tooltip;
  disabled?: boolean;
  rules?: Rule[];
  picker?: 'date' | 'year';
  formItemProps?: FormItemProps;
}

export interface FormDatePickerProps extends CommonDateProps {
  type?: 'date';
  placeholder?: string;
  datePickerProps?: DatePickerProps;
  initialValue?: Dayjs;
}

export interface FormRangePickerProps extends CommonDateProps {
  type: 'range';
  placeholder?: [string, string];
  datePickerProps?: RangePickerProps;
  initialValue?: [Dayjs, Dayjs];
}

export type FormDateProps = FormDatePickerProps | FormRangePickerProps;

function FormDate(props: FormDateProps) {
  const screens = Grid.useBreakpoint();
  const isMobile = !screens.md;
  const {
    label,
    name,
    formItemProps,
    initialValue,
    tooltip,
    placeholder,
    disabled = false,
    type = 'date',
    picker = 'date',
    datePickerProps,
    rules: propRules,
  } = props;

  const rules = useMemo(() => {
    const newRules = transformRules(
      {
        type: type === 'range' ? 'array' : 'object',
      },
      propRules,
    );
    return newRules;
  }, [propRules, type]);

  const getPopupContainer = useCallback(
    (node: any) => {
      if (isMobile && typeof document !== 'undefined') {
        return document.body;
      }
      return node.closest('.ant-card') || node.closest('.ant-form-item');
    },
    [isMobile],
  );

  const mergedRangePopupClassName = useMemo(() => {
    const existingPopupClassName = type === 'range' ? (datePickerProps as any)?.popupClassName : undefined;
    return [existingPopupClassName, isMobile ? 'form-date-range-popup' : undefined]
      .filter(Boolean)
      .join(' ');
  }, [datePickerProps, isMobile, type]);

  const mergedDatePopupClassName = useMemo(() => {
    const existingPopupClassName = type === 'date' ? (datePickerProps as any)?.popupClassName : undefined;
    return [existingPopupClassName, isMobile ? 'form-date-popup' : undefined].filter(Boolean).join(' ');
  }, [datePickerProps, isMobile, type]);

  return (
    <Form.Item
      name={name}
      label={label}
      rules={rules}
      tooltip={InfoTooltip.Config(tooltip)}
      initialValue={initialValue}
      validateFirst
      getValueFromEvent={(v: Dayjs | [Dayjs, Dayjs] | null) => {
        if (!v) return null;
        if (datePickerProps && 'showTime' in datePickerProps) return v;
        if (Array.isArray(v)) {
          return [v[0].startOf('day'), v[1]?.endOf('day')];
        }
        if (datePickerProps && 'showTime' in datePickerProps) {
          return v;
        }
        return v.startOf('day');
      }}
      {...formItemProps}
    >
      {type === 'range' ? (
        <RangePicker
          picker={picker}
          placeholder={placeholder as [string, string]}
          disabled={[disabled, disabled]}
          style={{ width: '100%' }}
          getPopupContainer={getPopupContainer}
          inputReadOnly={isMobile}
          popupClassName={mergedRangePopupClassName || undefined}
          placement={isMobile ? 'bottomLeft' : undefined}
          {...(datePickerProps as RangePickerProps)}
        />
      ) : (
        <DatePicker
          picker={picker}
          placeholder={placeholder as string}
          disabled={disabled}
          style={{ width: '100%' }}
          getPopupContainer={getPopupContainer}
          inputReadOnly={isMobile}
          popupClassName={mergedDatePopupClassName || undefined}
          placement={isMobile ? 'bottomLeft' : undefined}
          {...(datePickerProps as DatePickerProps)}
        />
      )}
    </Form.Item>
  );
}

export default FormDate;
